import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { isPlainObject } from "es-toolkit";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
  AnalyticsGranularity,
  AnalyticsResponse,
  AnalyticsSales,
  AnalyticsSalesRateTableType,
  AnalyticsSalesTotal
} from "src/app/backend/models/analytics";
import { omitUndefinedAndNullProperties } from "src/app/support/object";
import { environment } from "src/environments/environment";

@Injectable()
export class AnalyticsSalesBackend {
  private readonly http = inject(HttpClient);

  list<T = AnalyticsSales>({
    from,
    to,
    granularity,
    groupBy,
    rateTableType,
    limit,
    rateTableItemId,
    profitCenterId,
    fiscalProfileId,
    paymentMethodId,
    days,
    timeSlot
  }: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity | "none" | undefined;
    groupBy: string;
    rateTableType: AnalyticsSalesRateTableType | undefined;
    limit: number | undefined;
    rateTableItemId: string | undefined;
    profitCenterId: string | undefined;
    fiscalProfileId: string | undefined;
    paymentMethodId: string | undefined;
    days: number[] | undefined;
    timeSlot: [number, number] | undefined;
  }): Observable<AnalyticsResponse<T>> {
    return this.http
      .get<AnalyticsResponse<T>>(`${environment.snoots.api.analytics}/sales`, {
        params: omitUndefinedAndNullProperties({
          from,
          to,
          granularity: granularity || "yearly",
          groupBy,
          rateTableType,
          limit,
          rateTableItemId,
          profitCenterId,
          fiscalProfileId,
          paymentMethodId,
          daysOfWeek: days && days.length > 0 ? days.join(",") : undefined,
          timeslotFrom: timeSlot?.[0],
          timeslotTo: timeSlot?.[1]
        })
      })
      .pipe(
        map((response) => ({
          ...response,
          totalAverage: response.totalAverage || 0
        })),
        map((response) => {
          return {
            ...response,
            items: response.items.map((item: any) => {
              if (!isPlainObject(item.total)) {
                return {
                  ...item,
                  total: {
                    rateable: item.total
                  }
                };
              }

              return item;
            })
          };
        })
      );
  }

  export(params: {
    from: string;
    to: string;
    granularity: AnalyticsGranularity | undefined;
    groupBy: string;
    rateTableType: AnalyticsSalesRateTableType | undefined;
    limit: number | undefined;
    rateTableItemId: string | undefined;
    profitCenterId: string | undefined;
    fiscalProfileId: string | undefined;
    paymentMethodId: string | undefined;
    days: number[] | undefined;
    timeslot: [number, number] | null;
  }): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.analytics}/sales/exports`,
      omitUndefinedAndNullProperties({
        from: params.from,
        to: params.to,
        granularity: params.granularity,
        groupBy: params.groupBy,
        rateTableType: params.rateTableType,
        limit: params.limit,
        rateTableItemId: params.rateTableItemId,
        profitCenterId: params.profitCenterId,
        fiscalProfileId: params.fiscalProfileId,
        paymentMethodId: params.paymentMethodId,
        daysOfWeek:
          params.days && params.days.length > 0
            ? params.days.join(",")
            : undefined,
        timeslotFrom: params.timeslot?.[0],
        timeslotTo: params.timeslot?.[1]
      })
    );
  }
}
